/**
 * ModelFilter Component - Pass 4 Filtering Logic
 *
 * Renders model filter chips with Any/All segmented control.
 * Gated by RESULTS_NEW_DESIGN flag.
 * Pass 4: Adds selection state and filtering logic.
 *
 * Future passes will add:
 * - Pass 5: URL persistence
 */

import React, { useEffect, useMemo } from 'react';
import { logger } from '../../../lib/logging';
import type { Response } from '../../../lib/types/results';
import { getModelLogo } from '../../../utils/logoMapping';
import { formatModelDisplayName, inferProviderName } from '../../../utils/modelDisplayName';
import type { ModelMetadataEntry, ModelMetadataMap } from '../../../utils/modelDisplayName';

interface ModelInfo {
  modelId: string;
  displayName: string;
  providerName: string;
}

export interface ModelFilterProps {
  responses: Response[];
  modelDetails: Array<{ id: string; displayName: string; providerName: string }>;
  selectedModels: Set<string>;
  filterMode: 'any' | 'all';
  onFilterChange: (selectedModels: Set<string>, filterMode: 'any' | 'all') => void;
}

export const ModelFilter: React.FC<ModelFilterProps> = ({
  responses,
  modelDetails,
  selectedModels,
  filterMode,
  onFilterChange
}) => {
  console.log('[ModelFilter] COMPONENT RENDERING', {
    responsesCount: responses.length,
    modelDetailsCount: modelDetails.length,
    selectedModelsSize: selectedModels.size,
    filterMode
  });

  // Pass 2: Log mount for telemetry
  useEffect(() => {
    logger.info('model_filter_mount', undefined, {
      op: 'mount',
      component: 'ModelFilter',
      runKey: 'init',
      success: true
    });

    return () => {
      logger.info('model_filter_unmount', undefined, {
        op: 'unmount',
        component: 'ModelFilter',
        success: true
      });
    };
  }, []);

  // Build model metadata map
  const modelDetailsMap = useMemo(() => {
    const map: ModelMetadataMap = new Map();
    modelDetails.forEach(detail => {
      const entry: ModelMetadataEntry = {
        displayName: detail.displayName,
        providerName: detail.providerName
      };
      map.set(detail.id, entry);
    });
    return map;
  }, [modelDetails]);

  // Extract unique models from responses
  const availableModels = useMemo(() => {
    const unique = new Map<string, ModelInfo>();
    responses.forEach(response => {
      // Skip responses without valid modelId
      if (!response.modelId || response.modelId.trim() === '') {
        return;
      }

      if (!unique.has(response.modelId)) {
        unique.set(response.modelId, {
          modelId: response.modelId,
          displayName: formatModelDisplayName(response.modelId, modelDetailsMap),
          providerName: inferProviderName(response.modelId, modelDetailsMap)
        });
      }
    });
    return Array.from(unique.values());
  }, [responses, modelDetailsMap]);

  // Pass 3: Log available models (with debug info)
  useEffect(() => {
    logger.info('model_filter_models_extracted', undefined, {
      op: 'models_extracted',
      component: 'ModelFilter',
      modelCount: availableModels.length,
      models: availableModels.map(m => m.modelId),
      totalResponses: responses.length,
      responsesWithModelId: responses.filter(r => r.modelId && r.modelId.trim() !== '').length,
      modelDetailsCount: modelDetails.length
    });
  }, [availableModels, responses, modelDetails]);

  // Pass 4: Handle chip click
  const handleChipClick = (modelId: string) => {
    const newSelectedModels = new Set(selectedModels);
    if (newSelectedModels.has(modelId)) {
      newSelectedModels.delete(modelId);
      logger.info('model_filter_chip_deselected', undefined, {
        op: 'chip_deselected',
        component: 'ModelFilter',
        modelId,
        remainingCount: newSelectedModels.size
      });
    } else {
      newSelectedModels.add(modelId);
      logger.info('model_filter_chip_selected', undefined, {
        op: 'chip_selected',
        component: 'ModelFilter',
        modelId,
        selectedCount: newSelectedModels.size
      });
    }
    onFilterChange(newSelectedModels, filterMode);
  };

  // Pass 4: Handle filter mode change
  const handleFilterModeChange = (mode: 'any' | 'all') => {
    logger.info('model_filter_mode_changed', undefined, {
      op: 'mode_changed',
      component: 'ModelFilter',
      oldMode: filterMode,
      newMode: mode,
      selectedCount: selectedModels.size
    });
    onFilterChange(selectedModels, mode);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    onFilterChange(new Set(), filterMode);

    logger.info('model_filter_cleared', undefined, {
      op: 'filter_cleared',
      component: 'ModelFilter',
      previousCount: selectedModels.size,
      previousModels: Array.from(selectedModels)
    });
  };

  if (availableModels.length === 0) {
    console.log('[ModelFilter] RETURNING NULL - No available models');
    return null; // Don't show filter if no models available
  }

  console.log('[ModelFilter] RENDERING UI', {
    availableModelsCount: availableModels.length,
    models: availableModels.map(m => m.modelId)
  });

  return (
    <div>
      {/* Header with Any/All toggle */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs sm:text-sm font-semibold text-slate-400 uppercase tracking-wider">
          Filter by Model
        </h3>

        {/* Any/All Segmented Control */}
        <div className="inline-flex items-center bg-slate-700/50 rounded-lg p-0.5">
          <button
            onClick={() => handleFilterModeChange('any')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
              filterMode === 'any'
                ? 'bg-cyan-500 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            Any
          </button>
          <button
            onClick={() => handleFilterModeChange('all')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
              filterMode === 'all'
                ? 'bg-cyan-500 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            All
          </button>
        </div>
      </div>

      {/* Model Chips Grid - 2x3 layout */}
      <div className="grid grid-cols-3 gap-2">
        {availableModels.map(model => {
          const logoSrc = getModelLogo(model.modelId);
          const isSelected = selectedModels.has(model.modelId);

          return (
            <button
              key={model.modelId}
              onClick={() => handleChipClick(model.modelId)}
              title={model.displayName}
              aria-label={`Filter by ${model.displayName}`}
              className={`relative flex items-center justify-center aspect-square p-3 rounded-lg transition-all ${
                isSelected
                  ? 'bg-cyan-500/20 border-2 border-cyan-500'
                  : 'bg-slate-700/30 hover:bg-slate-700/50 border border-slate-600/50 hover:border-cyan-500/50'
              }`}
            >
              {/* Model Logo */}
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={model.displayName}
                  className="w-10 h-10 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <span className="text-lg font-bold text-slate-300">
                  {model.displayName.substring(0, 2).toUpperCase()}
                </span>
              )}

              {/* Small checkmark in corner when selected */}
              {isSelected && (
                <div className="absolute top-1 right-1">
                  <svg
                    className="w-3 h-3 text-cyan-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Clear link and model count footer */}
      <div className="flex items-center justify-between mt-3 pt-3">
        {/* Left: Clear link - only show if models selected */}
        {selectedModels.size > 0 ? (
          <button
            onClick={handleClearFilters}
            className="text-xs text-cyan-400 hover:text-cyan-300 underline transition-colors"
          >
            Clear
          </button>
        ) : (
          <div />
        )}

        {/* Right: X of Y models count */}
        <span className="text-xs text-slate-400">
          {selectedModels.size} of {availableModels.length} models
        </span>
      </div>
    </div>
  );
};
